import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { keys, isEmpty } from 'lodash';

/**
 * Interface for table row data
 */
interface TableRow {
  [key: string]: string | number | null | undefined;
}

/**
 * Interface for list data in complex format
 */
interface ListData {
  tableData?: TableRow[];
  isBarMic?: boolean;
  isTableMic?: boolean;
  [key: string]: any;
}

/**
 * Interface for content in complex format
 */
interface TableContent {
  listData?: ListData;
  [key: string]: any;
}

/**
 * Interface for table constants
 */
interface TableConstants {
  [key: string]: any;
}

/**
 * Interface for MuiTable component props
 */
interface MuiTableProps {
  // Complex format props
  content?: TableContent;
  constants?: TableConstants;
  
  // Simplified format props
  headers?: string[];
  rows?: (string | number | null | undefined)[][];
}

// Import the format utility for distance display
import { distanceToString } from '../../utilities/format';

/**
 * Transform device layout data for positioning detail tables
 *
 * @param layout - The raw device layout array
 * @param isMeter - Whether to use metric units
 * @param deviceType - Type of device (microphones, speakers, cameras)
 * @param microphoneHeight - Optional microphone height from area level (for microphones that store height at area level)
 * @param isBarMic - Whether the microphone is a bar type (fixed height, don't show height column)
 * @param isTableMic - Whether the microphone is a table type (fixed height, don't show height column)
 * @returns Transformed array with formatted positioning data
 */
export const transformPositioningData = (
  layout: any[],
  isMeter: boolean,
  deviceType?: string,
  microphoneHeight?: number,
  isBarMic?: boolean,
  isTableMic?: boolean,
  isBarCamera?: boolean,
  isCeilingSpkr?: boolean,
  isPendantSpkr?: boolean
): TableRow[] => {
  if (!layout || !Array.isArray(layout) || layout.length === 0) {
    return [];
  }

  // Hide height column only for bar and table microphones (they have fixed
  // heights). Ceiling-tile mics (BMA360D) carry a meaningful location.z and must
  // show Height (Z) per PIG-298 — mirrors the on-screen positioning detail, which
  // only suppresses height for the FIXED_HEIGHT (bar/table) mic categories.
  const tableWideShowHeight = deviceType !== 'microphones' || (!isBarMic && !isTableMic);

  return layout.map((device: any, index: number) => {
    const location = device.location || {};
    // Per-row override (set by reportDocument.js for CEILING expansion mics): their mounting
    // height is governed by their own model, not the primary's, so a bar primary must not
    // suppress it. See the comment at the call site.
    const shouldShowHeight = device._showHeight === true ? true : tableWideShowHeight;
    const row: TableRow = {
      '#': `${index + 1}`,
      'Model': device.modelName || device.model || 'Unknown',
      'Horizontal distance (X)': distanceToString(location.x || 0, isMeter),
      'Vertical distance (Y)': distanceToString(location.y || 0, isMeter)
    };

    // Add optional height field (only for devices that don't have fixed heights)
    // For speakers, use device.height property
    // For microphones, check location.z first, then area-level height
    if (shouldShowHeight) {
      if (deviceType === 'speakers' && device.height !== undefined) {
        row['Height (Z)'] = distanceToString(device.height, isMeter);
      } else if (location.z !== undefined) {
        row['Height (Z)'] = distanceToString(location.z, isMeter);
      } else if (device._heightFallback !== undefined) {
        // Expansion mics fall back to their OWN group height, not `microphoneHeight`
        // (which belongs to the physically unrelated primary mic).
        row['Height (Z)'] = distanceToString(device._heightFallback, isMeter);
      } else if (deviceType === 'microphones' && microphoneHeight !== undefined) {
        row['Height (Z)'] = distanceToString(microphoneHeight, isMeter);
      }
    }

    // Device-specific fields
    if (deviceType === 'microphones' || deviceType === 'cameras' ||
        (deviceType === 'speakers' && !isCeilingSpkr && !isPendantSpkr)) {
      if (device.horAngle !== undefined) {
        row['Horizontal angle'] = `${device.horAngle} deg`;
      }
      if (device.verAngle !== undefined) {
        row['Vertical angle'] = `${device.verAngle} deg`;
      }
    }

    if (deviceType === 'speakers') {
      if (device.powerTap70V !== undefined) {
        row['70v Tap'] = `${device.powerTap70V} Watts`;
      }
      if (device.gain !== undefined) {
        row['gain'] = `${device.gain} dB`;
      }
      if (device.delay !== undefined) {
        row['delay'] = `${device.delay} ms`;
      }
    }

    return row;
  });
};

/**
 * MUI Table section component that replaces the styled-components Table section
 * 
 * This component provides a Material UI equivalent of the original Table section component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiTableProps} props - Component props
 * @returns {JSX.Element | null} MUI Table section component or null if no valid data
 */
const MuiTable: React.FC<MuiTableProps> = (props) => {
  // Check if the data is provided in the simplified format or the complex format
  const { content, constants } = props;
  
  const styles = StyleSheet.create({
    table: {
      display: 'flex',
      width: 'auto',
      borderStyle: 'solid',
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0,
      marginBottom: 10,
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
    },
    tableHeader: {
      backgroundColor: '#f0f0f0',
      fontWeight: 'bold',
    },
    tableCell: {
      padding: 5,
      borderStyle: 'solid',
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
      flex: 1,
    },
    headerText: {
      fontSize: 10,
      fontWeight: 'bold',
    },
    cellText: {
      fontSize: 10,
    },
  });

  // Handle data in the complex format from reportDocument.js
  if (content && content.listData) {
    const { listData, isMeter, adjust, dimensions, microphoneHeight, isBarMic, isTableMic, isBarCamera, isCeilingSpkr, isPendantSpkr } = content;

    // Determine device type from constants if available
    let deviceType: string | undefined;
    if (constants?.deviceType) {
      deviceType = constants.deviceType;
    }

    // Handle both array format (from positioning details) and object format (from equipment list)
    let tableData: TableRow[];

    if (Array.isArray(listData)) {
      // This is positioning detail data - transform it
      tableData = transformPositioningData(listData, isMeter || false, deviceType, microphoneHeight, isBarMic, isTableMic, isBarCamera, isCeilingSpkr, isPendantSpkr);
    } else {
      // This is equipment list data - use tableData property
      tableData = listData.tableData || [];
    }
    
    
    // If tableData is empty, return null
    if (isEmpty(tableData)) {
      return null;
    }

    // Headers are the UNION of every row's keys (in first-seen order), not just row 0's.
    // Optional columns can belong to LATER rows only — a bar primary mic has no Height (Z) but a
    // ceiling expansion mic appended below it does — and keying off row 0 alone dropped the header
    // entirely, so those values rendered in an unlabeled overflow column. Same fix, and the same
    // reason, as EquipmentTableManager.tsx on the web side (PIG-585).
    const headers = Array.from(
      tableData.reduce((set: Set<string>, row) => {
        if (row) keys(row).forEach((k) => set.add(k));
        return set;
      }, new Set<string>())
    );

    return (
      <View style={styles.table}>
        {/* Table header */}
        <View style={[styles.tableRow, styles.tableHeader]}>
          {headers.map((header, index) => (
            <View key={`header-${index}`} style={styles.tableCell}>
              <Text style={styles.headerText}>{header}</Text>
            </View>
          ))}
        </View>

        {/* Table rows */}
        {tableData.map((row, rowIndex) => (
          <View key={`row-${rowIndex}`} style={styles.tableRow}>
            {headers.map((header, cellIndex) => (
              <View key={`cell-${rowIndex}-${cellIndex}`} style={styles.tableCell}>
                <Text style={styles.cellText}>{row[header]?.toString() || ''}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    );
  }
  
  // Handle data in the simplified format (headers and rows)
  if (props.headers && props.rows) {
    const { headers = [], rows = [] } = props;
    
    return (
      <View style={styles.table}>
        {/* Table header */}
        <View style={[styles.tableRow, styles.tableHeader]}>
          {headers.map((header, index) => (
            <View key={`header-${index}`} style={styles.tableCell}>
              <Text style={styles.headerText}>{header}</Text>
            </View>
          ))}
        </View>
        
        {/* Table rows */}
        {rows.map((row, rowIndex) => (
          <View key={`row-${rowIndex}`} style={styles.tableRow}>
            {row.map((cell, cellIndex) => (
              <View key={`cell-${rowIndex}-${cellIndex}`} style={styles.tableCell}>
                <Text style={styles.cellText}>{cell?.toString() || ''}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    );
  }
  
  // If no recognized data format is provided, return null
  return null;
};

export default MuiTable;
